import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send } from '../../svg';
import './AIChat.css';

const isUserLoggedIn = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return Boolean(user?.token);
  } catch (error) {
    return false;
  }
};

const AIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(isUserLoggedIn());
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm Usma AI Assistant. How can I help you today?",
      sender: 'ai',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Basic public questions shown to logged-out visitors only.
  const publicQuestions = [
    'What is UsmaApp?',
    'How do I log in?',
    'How do I create an account?',
    'How do I contact support?'
  ];

  const quickQuestions = [
    'How do I log in?',
    'How do I log out?',
    'How do I send feedback?',
    'How do I create a group?',
    'How do I block a user?',
    'How do I edit my profile?',
    'How do I delete my account?',
    'How do I create a publication with photo/video?',
    'How do I report a publication?',
    'How do I save a publication?',
    'How do I archive a publication?',
    'How do I edit or delete my publication?',
    'How do I add friends and send friend requests?',
    'How do I accept or reject friend requests?',
    'How do I unfriend someone?',
    'How do I follow or unfollow users?',
    'How do I find friends on the app?',
    'How do I start a chat?',    
    'How do I change privacy settings?',
    'How do I manage notifications?',
    'How do I contact support if a feature is not working?'
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Re-check login status each time the chat is opened so the right set of
  // quick questions is shown (public-only when logged out).
  useEffect(() => {
    if (isOpen) {
      setIsLoggedIn(isUserLoggedIn());
    }
  }, [isOpen]);

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim()) return;

    const userMessage = {
      text: messageText,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    setIsLoading(true);

    try {
      // Get user token if available; allow public chat without login
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const token = user?.token;

      const API_BASE = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/ai` : '/api/ai';
      const endpoint = token ? `${API_BASE}/chat` : `${API_BASE}/chat/public`;
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await axios.post(
        endpoint,
        { message: messageText },
        {
          headers
        }
      );

      if (response.data && response.data.success) {
        const aiResponse = {
          text: response.data.response || 'I received your message but have no response.',
          sender: 'ai',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, aiResponse]);
      } else {
        throw new Error(response.data?.message || 'Failed to get a response from the AI');
      }
    } catch (error) {
      console.error('Error sending message to AI:', error);
      
      let errorMessage = 'Sorry, I encountered an error. Please try again later.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 401) {
          errorMessage = 'Please log in to continue with account-specific chat help.';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid request. Please check your input.';
        } else if (error.response.status >= 500) {
          errorMessage = 'The server encountered an error. Please try again later.';
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else if (error.message) {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message;
      }
      
      const errorResponse = {
        text: errorMessage,
        sender: 'ai',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (question) => {
    if (isLoading) return;
    handleSendMessage(question);
  };

  return (
    <div className="ai-chat-container">
      {isOpen && (
        <div className="ai-chat-window">
          <div className="ai-chat-header">
            <div className="ai-chat-header-title-wrap">
              {/* <span className="ai-chat-badge ai-chat-badge-beta">Beta</span> */}
              <h3>UsmaApp Assistant</h3>
            </div>
            <button 
              className="ai-close-button"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              &times;
            </button>
          </div>
          
<div className="ai-messages-container">
  {messages.map((message, index) => (
    <div 
      key={index} 
      className={`ai-message ${message.sender} ${message.isError ? 'error' : ''}`}
    >
      <div className="ai-message-content">
        {message.text}
      </div>
      <div className="ai-message-time">
        {new Date(message.timestamp).toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}
      </div>
    </div>
  ))}
  {isLoading && (
    <div className="ai-message ai">
      <div className="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  )}
  <div ref={messagesEndRef} />
</div>

          <div className="ai-quick-questions">
            {(isLoggedIn ? quickQuestions : publicQuestions).map((question, index) => (
              <button
                key={index}
                className="quick-question"
                onClick={() => handleQuickQuestion(question)}
                disabled={isLoading}
              >
                {question}
              </button>
            ))}
          </div>

          <div className="ai-input-container">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type your question..."
              className="ai-chat-input"
              disabled={isLoading}
            />
            <button 
              className="ai-send-button"
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
            >
              <Send />
            </button>
          </div>
        </div>
      )}

      <button 
        className={`ai-chat-button ${isOpen ? 'active' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        type="button"
      >
        <span className="ai-icon">🤖</span>
      </button>
    </div>
  );
};

export default AIChat;
